import { ReactElement, useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Plotter, { PlotterContent } from '../../../components/Plotter';
import Skeleton from '../../../components/Skeleton';
import { chipVariants } from '../../../components/chipVariants';
import { cn } from '../../../utils/cn';
import { useUpdateSession } from '../../../hooks/useSessions';
import { Session } from '../../../types/Session';
import { speedSourceIsGps } from '../../../types/Run';

interface RunPlotterContext {
  run?: any;
  isRunsPending: boolean;
  isRunsFetching: boolean;
  calibration?: any;
  sessionData?: Session;
}

const SPEED_YMAX_KEY = 'run_speed_ymax_kmh';
const SPEED_YMAX_OPTIONS = [20, 40, 60, 80, 100] as const;
type SpeedYMaxValue = (typeof SPEED_YMAX_OPTIONS)[number] | 'auto';

function readSpeedYMax(
  plottingScales: Record<string, number | null> | null | undefined,
): SpeedYMaxValue {
  const raw = plottingScales?.[SPEED_YMAX_KEY];
  if (typeof raw === 'number'
      && (SPEED_YMAX_OPTIONS as readonly number[]).includes(raw)) {
    return raw as SpeedYMaxValue;
  }
  return 'auto';
}

interface BodyPosConfig {
  mode: 'delta' | 'absolute';
  showReference: boolean;
  head: boolean;
  chest: boolean;
  waist: boolean;
}

const BODYPOS_CONFIG_KEY = 'run-plotter-bodypos-config';

const DEFAULT_BODYPOS_CONFIG: BodyPosConfig = {
  mode: 'delta',
  showReference: false,
  head: true,
  chest: true,
  waist: false,
};

function getBodyPosConfig(): BodyPosConfig {
  try {
    const stored = localStorage.getItem(BODYPOS_CONFIG_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed.mode === 'string' && typeof parsed.head === 'boolean') {
        return { ...DEFAULT_BODYPOS_CONFIG, ...parsed };
      }
    }
  } catch {
    // ignore
  }
  return DEFAULT_BODYPOS_CONFIG;
}

export default function RunPlotter(): ReactElement {
  const {
    run, isRunsPending, calibration, sessionData,
  } = useOutletContext<RunPlotterContext>();

  const sessionId = sessionData?.id;
  const queryClient = useQueryClient();
  const updateSession = useUpdateSession(sessionId ?? '');

  const speedYMax: SpeedYMaxValue = useMemo(
    () => readSpeedYMax(sessionData?.plotting_scales),
    [sessionData?.plotting_scales],
  );

  const handleSpeedYMaxChange = useCallback(
    async (next: SpeedYMaxValue) => {
      if (!sessionId) {
        toast.error('No session context — cannot save scale');
        return;
      }
      const current = sessionData?.plotting_scales ?? {};
      const nextScales: Record<string, number | null> = { ...current };
      if (next === 'auto') {
        delete nextScales[SPEED_YMAX_KEY];
      } else {
        nextScales[SPEED_YMAX_KEY] = next;
      }
      try {
        await updateSession.mutateAsync({
          plotting_scales: nextScales,
        });
        await queryClient.invalidateQueries({
          queryKey: ['session', sessionId],
        });
      } catch (e) {
        console.error(e);
        toast.error('Failed to save y-axis scale');
      }
    },
    [
      sessionId, sessionData?.plotting_scales,
      updateSession, queryClient,
    ],
  );

  const speedYMaxControl = (
    <label
      className="cr-flex cr-items-center cr-gap-2
        cr-text-sm cr-text-gray-600"
    >
      Y-axis max
      <select
        value={String(speedYMax)}
        disabled={!sessionId || updateSession.isPending}
        onChange={(e) => {
          const v = e.target.value;
          handleSpeedYMaxChange(
            v === 'auto'
              ? 'auto'
              : (Number(v) as SpeedYMaxValue),
          );
        }}
        className="cr-rounded-md cr-border
          cr-border-gray-300 cr-px-2 cr-py-1
          cr-text-sm cr-text-gray-700
          focus:cr-outline-2
          focus:cr-outline-cirrusBlue-400
          disabled:cr-opacity-50"
      >
        {SPEED_YMAX_OPTIONS.map((v) => (
          <option key={v} value={v}>{v} km/h</option>
        ))}
        <option value="auto">Auto</option>
      </select>
    </label>
  );

  const [bodyPosConfig, setBodyPosConfig] = useState<BodyPosConfig>(getBodyPosConfig);

  // Persist config to localStorage
  useEffect(() => {
    localStorage.setItem(BODYPOS_CONFIG_KEY, JSON.stringify(bodyPosConfig));
  }, [bodyPosConfig]);

  const updateConfig = useCallback((patch: Partial<BodyPosConfig>) => {
    setBodyPosConfig((prev) => ({ ...prev, ...patch }));
  }, []);

  // Build available fields based on mode and body part selection
  const bodyPosFields = useMemo(() => {
    const fields: { label: string; field: string }[] = [];
    const { mode, showReference, head, chest, waist } = bodyPosConfig;
    const isDelta = mode === 'delta';

    if (head) {
      fields.push(
        isDelta
          ? { label: 'Head Delta', field: 'BodyPosHeadDelta_sG21_cm' }
          : { label: 'Head', field: 'BodyPosHead_sG21_cm' },
      );
      if (!isDelta && showReference) {
        fields.push({ label: 'Head Reference', field: 'BodyPosRefHead_sG11_cm' });
      }
    }
    if (chest) {
      fields.push(
        isDelta
          ? { label: 'Chest Delta', field: 'BodyPosChestDelta_sG21_cm' }
          : { label: 'Chest', field: 'BodyPosChest_sG21_cm' },
      );
      if (!isDelta && showReference) {
        fields.push({ label: 'Chest Reference', field: 'BodyPosRefChest_sG11_cm' });
      }
    }
    if (waist) {
      fields.push(
        isDelta
          ? { label: 'Waist Delta', field: 'BodyPosWaistDelta_sG21_cm' }
          : { label: 'Waist', field: 'BodyPosWaist_sG21_cm' },
      );
      if (!isDelta && showReference) {
        fields.push({ label: 'Waist Reference', field: 'BodyPosRefWaist_sG11_cm' });
      }
    }
    return fields;
  }, [bodyPosConfig]);

  // Which source the run's road speed was built from, and whether the other
  // one is there to overlay. The overlay chip keeps one field key in both
  // cases so the stored chip selection survives moving between runs.
  const speedSelection = run?.solve?.selections?.speed;
  const gpsIsSpeedSource = speedSourceIsGps(speedSelection);
  const gridded = run?.data_gridded;
  const hasAlternateSpeed = gpsIsSpeedSource
    ? Boolean(gridded?.WhlSensorLspd_v)
    : Boolean(gridded?.GpsSpeed_vG20 ?? gridded?.GpsSpeed_vG10);

  const speedFields = [
    { label: gpsIsSpeedSource ? 'Road Speed (GPS)' : 'Road Speed', field: 'RoadSpeed' },
    { label: 'Air Speed', field: 'AirSpeed' },
    ...(hasAlternateSpeed
      ? [{ label: gpsIsSpeedSource ? 'Wheel Speed' : 'GPS Speed', field: 'AlternateSpeed' }]
      : []),
  ];

  const speedFieldsSecondary = [{ label: 'Rider Power', field: 'RiderPwr_P' }];

  // The alternate source is off until asked for.
  const speedDefaultFields = ['RoadSpeed', 'AirSpeed', 'RiderPwr_P'];

  // The channel maths reads `gps_speed` to know whether the wheel channel is
  // already in m/s. It has to say what THIS run used, which the calibration
  // cannot: the run may have fallen back, or the calibration changed since.
  const speedCalibration = useMemo(
    () => ({ ...calibration, gps_speed: gpsIsSpeedSource }),
    [calibration, gpsIsSpeedSource],
  );

  const speedSourceNote = gpsIsSpeedSource ? (
    <span
      className="cr-rounded-md cr-bg-amber-50 cr-px-2 cr-py-1
        cr-text-sm cr-text-amber-800"
    >
      {speedSelection?.gps_fallback
        ? 'Road speed is from GPS — no wheel speed sensor was found'
        : 'Road speed is from GPS, as selected for this run'}
    </span>
  ) : null;

  const baroFields = [
    { label: 'Barometric Pressure Raw', field: 'Baro_pG21' },
    { label: 'Barometric Pressure', field: 'Baro_pG31' },
  ];

  const lapProps = {
    segmentTimestamps: run?.segment_ts,
    excludeCandidates: run?.exclude_candidates_ts,
    excludeTs: run?.exclude_ts,
  };

  return (
    <div className="cr-flex cr-flex-col cr-gap-4">
      <Plotter
        isPending={isRunsPending}
        availableFields={speedFields}
        data={run?.data_gridded || null}
        title="Road and Air Speed vs Time"
        storageKey="run-plotter-speed-selections"
        yAxisTitle="Speed (km/h)"
        yAxisUnit="km/h"
        availableFieldsSecondary={speedFieldsSecondary}
        yAxisTitleRight="Power (W)"
        yAxisUnitRight="W"
        calibration={speedCalibration}
        defaultFields={speedDefaultFields}
        {...lapProps}
        runId={run?.id}
        yAxisMax={speedYMax === 'auto' ? undefined : speedYMax}
        headerExtra={(
          <div className="cr-flex cr-flex-wrap cr-items-center cr-justify-end cr-gap-3">
            {speedSourceNote}
            {speedYMaxControl}
          </div>
        )}
      />

      <div className="cr-space-y-6">
        <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
          <h2 className="cr-mb-4 cr-text-xl cr-font-semibold">Body Position vs Time</h2>

          {/* Top Row: Delta/Absolute radio + Show Reference checkbox */}
          <div className="cr-flex cr-items-center cr-gap-6 cr-mb-3">
            <label className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer">
              <input
                type="radio"
                name="bodypos-mode"
                checked={bodyPosConfig.mode === 'delta'}
                onChange={() => updateConfig({ mode: 'delta' })}
                className="cr-h-4 cr-w-4 cr-text-blue-600"
              />
              <span className="cr-text-sm cr-font-medium">Delta</span>
            </label>
            <label className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer">
              <input
                type="radio"
                name="bodypos-mode"
                checked={bodyPosConfig.mode === 'absolute'}
                onChange={() => updateConfig({ mode: 'absolute' })}
                className="cr-h-4 cr-w-4 cr-text-blue-600"
              />
              <span className="cr-text-sm cr-font-medium">Absolute</span>
            </label>
            {bodyPosConfig.mode === 'absolute' && (
              <label className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer cr-ml-4">
                <input
                  type="checkbox"
                  checked={bodyPosConfig.showReference}
                  onChange={() => updateConfig({ showReference: !bodyPosConfig.showReference })}
                  className="cr-h-4 cr-w-4 cr-rounded cr-border-gray-300 cr-text-blue-600"
                />
                <span className="cr-text-sm cr-font-medium">Show Reference</span>
              </label>
            )}
          </div>

          {/* Lower Row: Head / Chest / Waist chip buttons */}
          <div className="cr-flex cr-gap-2 cr-mb-2">
            <div
              className={cn('cr-select-none', chipVariants({ active: bodyPosConfig.head }))}
              onClick={() => updateConfig({ head: !bodyPosConfig.head })}
            >
              Head
            </div>
            <div
              className={cn('cr-select-none', chipVariants({ active: bodyPosConfig.chest }))}
              onClick={() => updateConfig({ chest: !bodyPosConfig.chest })}
            >
              Chest
            </div>
            <div
              className={cn('cr-select-none', chipVariants({ active: bodyPosConfig.waist }))}
              onClick={() => updateConfig({ waist: !bodyPosConfig.waist })}
            >
              Waist
            </div>
          </div>

          <Skeleton isPending={isRunsPending}>
            {bodyPosFields.length > 0 ? (
              <PlotterContent
                availableFields={bodyPosFields}
                data={run?.data_gridded || null}
                storageKey="run-plotter-bodypos-selections"
                yAxisTitle="Body Position (cm)"
                yAxisUnit="cm"
                hideChipSelector
                hideLapSelector
                calibration={calibration}
                {...lapProps}
              />
            ) : (
              <div className="cr-mt-4 cr-text-gray-500">
                Select at least one body part (Head, Chest, or Waist) to display data.
              </div>
            )}
          </Skeleton>
        </div>
      </div>

      <Plotter
        isPending={isRunsPending}
        availableFields={baroFields}
        data={run?.data_gridded || null}
        title="Barometric Pressure vs Time"
        storageKey="run-plotter-baro-selections"
        yAxisTitle="Pressure (Pa)"
        yAxisUnit="Pa"
        calibration={calibration}
      />
    </div>
  );
}
